import { formatAssistantContext } from 'ai-assistant/context/formatAssistantContext';
import { AssistantContextSnapshot } from 'ai-assistant/context/AssistantContext';
import { AssistantSyncPayload } from 'ai-assistant/utils/assistantModelSync';
import { IChatMessage } from '../models/chat-message';
import { IStreamingCompletionResponse, StreamingCompletionResponse, StreamingEvent } from '../models/completion-response';
import { UUID } from '../utils/uuid';
import { getChatAuthHeaders } from './chat-auth-service';

export type ChatAIMode = 'ask' | 'edit';

export interface ChatRequestOptions {
  aiMode?: ChatAIMode;
  assistantSync?: AssistantSyncPayload;
}

export interface IChatService {
  sendMessage(
    messages: IChatMessage[],
    context: AssistantContextSnapshot,
    options?: ChatRequestOptions
  ): Promise<IStreamingCompletionResponse>;
}

export type TitleResponse = { title: string };

export const serializeMessagesToPrompt = (messages: IChatMessage[], context: AssistantContextSnapshot): string => {
  const conversation = messages
    .map(msg => `${msg.role}: ${msg.content}`)
    .join('\n\n');

  const contextText = formatAssistantContext(context);

  if (!contextText) {
    return conversation;
  }

  return `Context:\n${contextText}\n\nConversation:\n${conversation}`;
};

const getLatestUserMessageContent = (messages: IChatMessage[]): string => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role === 'user') {
      return message.content ?? '';
    }
  }

  return '';
};

export class ChatService implements IChatService {
  private sessionId: string;

  constructor(sessionId: string) {
    this.sessionId = sessionId;
  }

  async sendMessage(
    messages: IChatMessage[],
    context: AssistantContextSnapshot,
    _options?: ChatRequestOptions,
  ): Promise<IStreamingCompletionResponse> {
    const serializedPrompt = serializeMessagesToPrompt(messages, context);

    const response = await fetch('...', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({
        sessionId: this.sessionId,
        prompt: serializedPrompt,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Streaming API request failed:', response.status, errorText);
      throw new Error(`Streaming API request failed: ${response.status} ${errorText}`);
    }

    return new StreamingCompletionResponse(response);
  }
}
export class ESETChatService implements IChatService {
  private sessionId?: UUID;
  private readonly onSessionIdChange?: (sessionId: UUID) => void;

  constructor(
    sessionId?: UUID,
    onSessionIdChange?: ((sessionId: UUID) => void)
  ) {
    this.sessionId = sessionId;
    this.onSessionIdChange = onSessionIdChange;
  }

  private buildRequestUrl(context?: AssistantContextSnapshot, aiMode?: ChatAIMode): string {
    const url = new URL('/chat', window.location.origin);
    if (this.sessionId) {
      url.searchParams.set('session_id', this.sessionId);
    }
    if (aiMode) {
      url.searchParams.set('mode', aiMode);
    }

    const modelEntry = context?.['model.current'];
    const modelData = modelEntry?.data as any | undefined;
    const currentModel = (modelData && 'currentModel' in modelData) ? (modelData as any).currentModel : modelData?.currentModel;
    const model = currentModel ?? modelData?.currentModel ?? modelData;

    const modelName = model?.modelName;
    const draftId = model?.draftId;
    const modelType = model?.modelType ?? modelData?.modelType;
    const modelKey = modelName ? `saved:${modelName}` : draftId ? `draft:${draftId}` : '';
    const isUnsaved = Boolean(!modelName && draftId);

    if (modelName || draftId || modelType) {
      url.searchParams.set(
        'model_info',
        JSON.stringify({
          model_name: modelName || '',
          draft_id: draftId || '',
          model_type: modelType || '',
          model_key: modelKey,
          is_unsaved: isUnsaved,
        }),
      );
    }

    return url.toString();
  }

  async sendMessage(
    messages: IChatMessage[],
    context: AssistantContextSnapshot,
    options?: ChatRequestOptions
  ): Promise<IStreamingCompletionResponse> {
    const latestUserMessage = getLatestUserMessageContent(messages);
    const contextText = formatAssistantContext(context);
    const authHeaders = await getChatAuthHeaders();

    const response = await fetch(this.buildRequestUrl(context, options?.aiMode), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...authHeaders,
      },
      body: JSON.stringify({
        chat_in: latestUserMessage,
        context: contextText || undefined,
        assistant_sync: options?.assistantSync,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ESET chatbot API request failed:', response.status, errorText);
      throw new Error(`ESET chatbot API request failed: ${response.status} ${errorText}`);
    }

    const nextSessionId = response.headers.get('X-Session-ID');
    if (nextSessionId) {
      const normalizedNextSessionId = nextSessionId as UUID;
      const sessionChanged = this.sessionId !== normalizedNextSessionId;
      this.sessionId = normalizedNextSessionId;
      if (sessionChanged) {
        this.onSessionIdChange?.(normalizedNextSessionId);
      }
    }

    return new StreamingCompletionResponse(response);
  }
}

export async function generateChatTitle(
  sessionId: string,
  messages: IChatMessage[],
): Promise<string> {
  const url = new URL('/chat/title', window.location.origin);
  const authHeaders = await getChatAuthHeaders();

  const trimmedMessages = messages
    .filter(message => message.role === 'user' || message.role === 'assistant')
    .slice(0, 6)
    .map(message => ({
      role: message.role,
      content: message.content ?? '',
    }));

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify({
      session_id: sessionId,
      messages: trimmedMessages,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('ESET chat title generation failed:', response.status, errorText);
    throw new Error(`Unable to generate chat title (${response.status}).`);
  }

  const payload: TitleResponse = await response.json();
  return payload?.title ?? 'Untitled chat';
}

export class MockChatService implements IChatService {
  constructor(private readonly sessionId?: string) { }

  async sendMessage(
    __messages__: IChatMessage[],
    __context__: AssistantContextSnapshot,
    __options__?: ChatRequestOptions
  ): Promise<IStreamingCompletionResponse> {
    await new Promise(resolve => setTimeout(resolve, 100));

    console.log('__context__', __context__);

    const contextText = formatAssistantContext(__context__);
    const contextPrefix = contextText ? `Context received:\n${contextText}\n\n` : '';

    class MockStreamingCompletionResponse implements IStreamingCompletionResponse {
      async onEvent(callback: (event: StreamingEvent) => void): Promise<void> {
        const loremIpsum = "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.";
        const chunks: string[] = [];

        if (contextPrefix) {
          chunks.push(contextPrefix);
        }

        for (const chunk of loremIpsum.split(' ')) {
          chunks.push(`${chunk} `);
        }

        for (const chunk of chunks) {
          await new Promise(resolve => setTimeout(resolve, 50));
          callback({ event: 'message', data: chunk });
        }
      }
    }

    return new MockStreamingCompletionResponse();
  }
}
